/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/no-misused-promises */
import * as Sentry from "@sentry/nextjs";
import { Box, Flex, LegacyText } from "flicket-ui";
import { FormikHelpers } from "formik";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useContext, useEffect } from "react";
import { Footer, Layout, PageContainer, Seo, Status } from "~components";
import EventHeader from "~components/common/common.EventHeader";
import Image from "~components/common/Image";
import { WaitlistForm } from "~components/events/waitlist";
import { WaitListFormValues } from "~components/events/waitlist/interface";
import { BaseContext } from "~context";
import {
  CompetitionSeoProps,
  competitionSeo,
} from "~features/competitions/competitionSeo";
import { useAccount, useIsMobile } from "~hooks";
import { useCampaignReportingOpen } from "~hooks/useBroadastCampaignReporting";
import { useGoogleAnalyticsPageView } from "~hooks/useGoogleAnalyticsPageView";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import { useReferralCode } from "~hooks/useReferralCode";
import {
  getError,
  handlePromise,
  noOrgFoundRedirect,
  SDKGeneratorFn,
  showToast,
  trackLeadFB,
} from "~lib";
import { formatEventDate } from "~lib/helpers/formatDate";
import { getServerSideOrganization, setCacheHeaders } from "~lib/server";
import { OptionalSeekaProvider, seekaMergeProfile } from "~lib/seeka";
import { AttributeNames, PageName } from "~telemetry/enums/AttributeNames";
import usePageLoad from "~telemetry/traces/usePageLoad";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import {
  CurrentOrganizationQuery,
  EventDocument,
  EventQuery,
  JoinWaitlistMutation,
} from "~graphql/typed-document-nodes";
import { HighDemandRedirect } from "~lib/helpers/handleHighDemandRedirect";
import { ConvergeSdk } from "@seeka-labs/converge";
import { useQuery } from "~hooks/useQuery";

export const getServerSideProps: GetServerSideProps<CompetitionSeoProps> = async (
  context
) => {
  try {
    const { data, error } = await getServerSideOrganization(context.req);
    if (error) {
      return noOrgFoundRedirect(context.res);
    }

    setCacheHeaders(context.res);

    return await competitionSeo(
      context.req,
      data.currentOrganization.id,
      context.params?.eventId as string
    );
  } catch (error) {
    if (error?.message === "CURRENT_ORGANIZATION_TIMEOUT") {
      console.error("CURRENT_ORGANIZATION_TIMEOUT");
      return HighDemandRedirect(context.res, context.resolvedUrl);
    }
    return noOrgFoundRedirect(context.res);
  }
};

const hasSessionStorage = typeof sessionStorage !== "undefined";

const Registration: NextPage<CompetitionSeoProps> = ({
  activeCompetition: { data: activeCompetition },
  eventTitle: { data: eventTitle },
}) => {
  const router = useRouter();
  const { organization } = useContext(BaseContext);

  const { data: eventData, error: eventError } = useQuery(EventDocument, {
    id: router?.query?.eventId as string,
  });

  const event = eventData?.event;

  const waitlistEndDate = event?.waitlistEndDate
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      new Date(event.waitlistEndDate)
    : null;

  useCampaignReportingOpen(!!event);

  useEffect(() => {
    if (event?.hasPublicRelease) {
      void router.push(`/events/${router.query.eventId}/reservation`);
    }
  }, [event?.hasPublicRelease]);

  useEffect(() => {
    if (event && !event?.isWaitlistAvailable) {
      void router.push(`/events/${router.query.eventId}`);
    }
  }, [event?.isWaitlistAvailable]);

  useGoogleAnalyticsPageView(
    !!event?.id,
    PageName.EVENT_WAITLIST_PAGE,
    event?.id
  );

  usePageLoad(
    PageName.EVENT_WAITLIST_PAGE,
    {
      forwardToNest: true,
      eventId: event?.id,
      attributes: {
        [AttributeNames.FLICKET_COMPETITION_ID]: activeCompetition?.id,
      },
    },
    // Only fire the page-load when:
    // - the event is loaded
    // - the active competition is loaded (if there is one)
    // - no redirect will occur due to a) a public release being available or no waitlist available
    !!(event?.id && !event?.hasPublicRelease && event?.isWaitlistAvailable)
  );

  let title = activeCompetition
    ? activeCompetition.title
    : "Register to be notified when tickets go on sale!";

  let endsAtLabel = waitlistEndDate
    ? `Registration closes ${formatEventDate(
        waitlistEndDate.toISOString(),
        "datetime",
        {
          timeZone: event?.venue?.timezone,
          locale: event?.venue?.locale,
        }
      )}`
    : "";

  if (organization?.id === "79efd61b-292a-4237-bc1a-551fd8104774") {
    title =
      "Australian Motorsport Innovation Precinct (AMIP) Launch Registration";
  }

  // Temp Duco - Manuka Doctor: DAVID NYIKA ROAD TO THE TITLE
  // Remove after 2024-09-15
  if (router?.query?.eventId === "19167bba-5ef4-4568-a470-4720f0be35bb") {
    title = "Register for first access to secure your corporate table";
    endsAtLabel = "Registrations close soon";
  }

  // ASB Classic
  if (organization?.id === "c8f276b9-87d5-42c8-ba3f-102089545cde") {
    title = "Register for offers, updates and exclusive content";
    endsAtLabel = "";
  }

  return (
    <Status loading={!event && !eventError} error={eventError}>
      <OptionalSeekaProvider seekaConfig={event?.seekaConfig}>
        <Seo
          title={`Register for ${eventTitle}`}
          description={undefined} // use default event description
          options={{
            openGraph: {
              title,
            },
          }}
        />
        <RegistrationPage
          event={event}
          title={title}
          endsAtLabel={endsAtLabel}
        />
      </OptionalSeekaProvider>
    </Status>
  );
};

export const handleRegistrationFormSubmit = async (
  values: WaitListFormValues,
  actions: FormikHelpers<WaitListFormValues>,
  context: {
    event: EventQuery["event"];
    organization: CurrentOrganizationQuery["currentOrganization"];
    releaseId?: string;
    recaptchaSdkGenerator: SDKGeneratorFn;
    referralCode: string;
    referralCodeVersion: string;
    converge: ConvergeSdk;
    revaildateUser: ({
      trackUserLogin,
    }?: {
      trackUserLogin: boolean;
    }) => Promise<void>;
  },
  onSuccess?: (joinWaitlistResult: JoinWaitlistMutation) => void
) => {
  const {
    event,
    organization,
    recaptchaSdkGenerator,
    referralCode,
    referralCodeVersion,
    converge,
    revaildateUser,
    releaseId,
  } = context;

  const { data, error } = await handlePromise(async () => {
    trackLeadFB(
      values,
      event.title,
      organization,
      event?.pixelId ?? organization?.marketing?.pixelId
    );

    const sdkFn = await recaptchaSdkGenerator("joinWaitlist");

    // The order of the merge profile compared to the track.lead doesn't matter
    // as seeka reconciles it later. So fire the lead first to give it the best chance
    // of completing in case the user navigates away before the request completes
    void converge?.track?.lead({
      sourceContentName: "Registration form",
    });

    const joinWaitlistResult = await sdkFn({
      orgId: organization.id,
    }).joinWaitlist({
      input: {
        userAcquisition: { referralCodeId: referralCode },
        eventId: event.id,
        ...values,
        ...(referralCode && referralCodeVersion === "2"
          ? { referralCode }
          : {}),
        ...(releaseId
          ? {
              releaseId,
            }
          : {}),
      },
    });

    seekaMergeProfile(
      converge,
      values,
      organization.address?.country,
      joinWaitlistResult?.joinWaitlist
    );

    return joinWaitlistResult;
  });

  actions.setSubmitting(false);

  if (error) {
    showToast(getError(error, "graphQL"), "error");
    Sentry.captureException(error);
    return;
  }
  showToast("You have been added to the waitlist.");
  hasSessionStorage && sessionStorage.removeItem("waitlist-saved-values");
  await revaildateUser();
  onSuccess(data);
};

function RegistrationPage({
  event,
  endsAtLabel,
  title,
}: {
  event: EventQuery["event"];
  endsAtLabel: string;
  title: string;
}) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const { referralCode, referralCodeVersion } = useReferralCode();
  const { organization } = useContext(BaseContext);
  const { revaildateUser } = useAccount();

  const converge = useSeekaConverge();

  const logo = event?.logo;

  return (
    <>
      <EventHeader
        type="event"
        venue={event?.venue}
        title={event?.title}
        startDate={event?.startDate}
        endDate={event?.endDate}
        goBack={() => void router.push("/")}
        accessibility={event?.accessibility}
        supportInfo={organization?.supportInfo}
        image={event?.logo}
        description={event?.description}
        coordinates={`${event?.venue?.location?.lat},${event?.venue?.location?.lng}`}
      />
      <PageContainer
        backgroundImage={event?.backgroundImage}
        backgroundImageFullPage={event?.backgroundImageFullPage}
        bannerImage={isMobile ? undefined : event?.logo}
        footer={<Footer />}
        containerProps={{
          mt: { _: 0, md: 6 },
        }}
      >
        <Layout
          layoutType="overlay"
          bg={"white"}
          mt={{ _: 3, md: 0 }}
          pb={{ md: "120px" as any }}
        >
          {event && (
            <Box mt={{ _: logo ? 5 : 8, md: "-32px" as any }}>
              {logo && isMobile && <Image alt={title} mb={4} image={logo} />}
              <Flex
                px={{ _: 0, md: 2 }}
                pb={3}
                justifyContent={"center"}
                flexDirection="column"
                alignItems={"center"}
              >
                <LegacyText
                  textAlign={"center"}
                  lineHeight="normal"
                  fontSize={{ _: 6, md: 7 }}
                  fontWeight="extraBold"
                  color="N700"
                >
                  {title}
                </LegacyText>
                {endsAtLabel && (
                  <LegacyText marginTop={4}>{endsAtLabel}</LegacyText>
                )}
              </Flex>
            </Box>
          )}
          <Flex
            width={"580px"}
            maxWidth={{ default: "100%", sm: 580 }}
            alignSelf="center"
          >
            <WaitlistForm
              waitlistType="registration"
              handleSubmit={async (values, actions) =>
                handleRegistrationFormSubmit(
                  values,
                  actions,
                  {
                    event,
                    organization,
                    recaptchaSdkGenerator,
                    referralCode,
                    referralCodeVersion,
                    converge,
                    revaildateUser,
                  },
                  (data) => {
                    void router.push(
                      `/events/${router.query.eventId}/share?wuid=${data?.joinWaitlist?.id}`
                    );
                  }
                )
              }
              presaleInformation={event?.presaleInformation}
              customFields={event?.customFields?.filter((customField) =>
                customField.targetArea.includes("registration")
              )}
            />
          </Flex>
        </Layout>
      </PageContainer>
    </>
  );
}

export default Registration;
